import React, { useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import { useFormikContext } from 'formik';
import { lookupsJson, LiabilityType } from '@ourbranch/lookups';

import Section from 'core/components/section';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import InfoIcon from 'core/assets/svg/ic-info-white.svg';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import BusinessActivities from './sections/business-activities';
import CareProvidedToOthers from './sections/care-provided-to-others';
import CustomFarming from './sections/custom-farming';
import CustomFeeding from './sections/custom-feeding';
import DomesticEmployees from './sections/domestic-employees';
import EmployersLiability from './sections/employers-liability';
import IncidentalBusinessPursuits from './sections/incidental-business-pursuits';
import Occupancy from './sections/occupancy';
import AdditionalPropertiesExtensions from './sections/additional-properties-extensions';
import AdditionalAssetsExtensions from './sections/additional-assets-extensions';
import useStyles from './liability-coverages-styles';

const liabilityTypeOptions = lookupsJson.farm.liabilityTypeOptions;
const farmOperationsOptions = lookupsJson.farm.farmOperations;
const eachOccurrenceLimitOptions = lookupsJson.farm.eachOccurrenceLimit;
const medicalExpensePerPersonLimitOptions = lookupsJson.farm.medicalExpensePerPersonLimit;
const generalAggregateLimitOptions = lookupsJson.farm.generalAggregateLimit;

const LiabilityCoverages = () => {
  const classes = useStyles();
  const { values } = useFormikContext();
  const { disableForm, setActiveForm } = useActiveFormState('LiabilityCoverages');

  const setLiabilityToActive = useCallback(() => {
    setActiveForm('LiabilityCoverages');
  }, [setActiveForm]);

  const totalAcres = (values.farmLocations || []).reduce((acc, loc) => acc + (loc?.numberOfAcres || 0), 0);

  const typeSelected = values.includeFarmLiability && values.farmLiabilityCoverage?.type;
  const showPersonal = typeSelected === LiabilityType.Personal;
  const showCommercial = typeSelected === LiabilityType.Commercial;

  console.log('values', values);

  return (
    <Section title="Liability" type="SubSection">
      <Grid container className={classes.liabilityContainer} alignItems="flex-start">
        <Field
          xs={12}
          type="switch"
          id="includeFarmLiability"
          name="includeFarmLiability"
          mode="dark"
          label="Liability Coverage"
          disabled={disableForm}
          onChange={setLiabilityToActive}
        />
        {values.includeFarmLiability && (
          <Field
            xs={12}
            type="select"
            id="farmLiabilityCoverage.type"
            name="farmLiabilityCoverage.type"
            mode="dark"
            label="Type of liability coverage"
            options={liabilityTypeOptions}
            disabled={disableForm}
            onChange={setLiabilityToActive}
          />
        )}
        {typeSelected && (
          <Grid container spacing={2}>
            {/* farm details section */}
            <Grid item xs={12}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Farm details
              </Label>
            </Grid>
            <Grid item xs={12} className={classes.acresRow}>
              <span className={classes.acresItem}>
                <img src={InfoIcon} alt="Notification Icon" />
                <span>Total number of acres</span>
              </span>
              <span className={classes.acresItem}>
                <span className={classes.acresLabel}>ACRES</span>
                <span className={classes.acresValue}>{totalAcres}</span>
              </span>
            </Grid>
            <Field
              xs={12}
              type="select"
              id="farmLiabilityCoverage.farmOperations"
              name="farmLiabilityCoverage.farmOperations"
              mode="dark"
              label="Primary farm operations"
              options={farmOperationsOptions}
              disabled={disableForm}
              onChange={setLiabilityToActive}
            />

            {/* coverages section */}
            <Label type="formSubTitle" className={classes.sectionDivider}>
              Coverages
            </Label>
            <Field
              xs={4}
              type="select"
              id="farmLiabilityCoverage.coverageL"
              name="farmLiabilityCoverage.coverageL"
              mode="dark"
              label="Farm personal liability (L)"
              options={eachOccurrenceLimitOptions}
              disabled={disableForm}
              onChange={setLiabilityToActive}
            />
            <Field
              xs={4}
              type="select"
              id="farmLiabilityCoverage.coverageM"
              name="farmLiabilityCoverage.coverageM"
              mode="dark"
              label="Medical expense per person limit (M)"
              options={medicalExpensePerPersonLimitOptions}
              disabled={disableForm}
              onChange={setLiabilityToActive}
            />
            <Field
              xs={4}
              type="select"
              id="farmLiabilityCoverage.annualAggregateLimit"
              name="farmLiabilityCoverage.annualAggregateLimit"
              mode="dark"
              label="General aggregate limit"
              options={generalAggregateLimitOptions}
              disabled={disableForm}
              onChange={setLiabilityToActive}
            />
            <Field
              xs={showPersonal ? 4 : 6}
              type="value"
              id="farmLiabilityCoverage.farmPollutionLimit"
              name="farmLiabilityCoverage.farmPollutionLimit"
              mode="dark"
              label="Limited farm pollution"
              formatter={(val) => (val != null ? `$${Number(val).toLocaleString()}` : val)}
            />
            <Field
              xs={showPersonal ? 4 : 6}
              type="value"
              id="farmCoverage.fungiLiabilityLimit"
              name="farmCoverage.fungiLiabilityLimit"
              mode="dark"
              label="Limited fungi, wet or dry rot, or bacteria liability coverage"
              formatter={(val) => (val != null ? `$${Number(val).toLocaleString()}` : val)}
            />
            {showPersonal && (
              <Field
                xs={4}
                type="value"
                id="farmLiabilityCoverage.personalInjury"
                name="farmLiabilityCoverage.personalInjury"
                mode="dark"
                label="Personal injury"
                formatter={(val) => (val ? 'Included' : 'Not included')}
              />
            )}

            {/*  exclusions section  */}
            <Label type="formSubTitle" className={classes.sectionDivider}>
              Exclusions
            </Label>
            <Grid container className={classes.exclusionsContainer}>
              <Field
                xs={12}
                type="switch"
                id="farmLiabilityCoverage.hasExclusions"
                name="farmLiabilityCoverage.hasExclusions"
                mode="dark"
                label="Specific activity or location exclusion"
                disabled={disableForm}
                onChange={setLiabilityToActive}
              />
              {values.farmLiabilityCoverage?.hasExclusions && (
                <>
                  <Grid item xs={12}>
                    <Label type="infoLabelWhite">Apply exclusion to:</Label>
                  </Grid>
                  <Field
                    xs={12}
                    type="checkbox"
                    id="farmLiabilityCoverage.exclusions.excludeActivities"
                    name="farmLiabilityCoverage.exclusions.excludeActivities"
                    mode="dark"
                    label="Specific activities"
                    disabled={disableForm}
                    onChange={setLiabilityToActive}
                  />
                  {values.farmLiabilityCoverage?.exclusions?.excludeActivities && (
                    <Field
                      xs={12}
                      type="textarea"
                      id="farmLiabilityCoverage.exclusions.excludedActivitiesDescription"
                      name="farmLiabilityCoverage.exclusions.excludedActivitiesDescription"
                      label="Description of the specific activities being excluded (required)"
                      mode="dark"
                      disabled={disableForm}
                      onChange={setLiabilityToActive}
                    />
                  )}
                  <Field
                    xs={12}
                    type="checkbox"
                    id="farmLiabilityCoverage.exclusions.excludeLocations"
                    name="farmLiabilityCoverage.exclusions.excludeLocations"
                    mode="dark"
                    label="Specific locations"
                    disabled={disableForm}
                    onChange={setLiabilityToActive}
                  />
                  {values.farmLiabilityCoverage?.exclusions?.excludeLocations && (
                    <Field
                      xs={12}
                      type="textarea"
                      id="farmLiabilityCoverage.exclusions.excludedLocationsDescription"
                      name="farmLiabilityCoverage.exclusions.excludedLocationsDescription"
                      label="Description of the specific locations being excluded (required)"
                      mode="dark"
                      disabled={disableForm}
                      onChange={setLiabilityToActive}
                    />
                  )}
                </>
              )}
            </Grid>

            {/* personal liability sub-sections */}
            {showPersonal && (
              <>
                <BusinessActivities disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <CareProvidedToOthers disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <CustomFarming disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <DomesticEmployees disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <EmployersLiability disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <IncidentalBusinessPursuits disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <Occupancy disabled={disableForm} onChangeActive={setLiabilityToActive} />
              </>
            )}
            {/* commercial liability sub-sections */}
            {showCommercial && (
              <>
                <CustomFarming disabled={disableForm} onChangeActive={setLiabilityToActive} />
                <CustomFeeding disabled={disableForm} onChangeActive={setLiabilityToActive} />
              </>
            )}
            {/* shared sub-sections (personal + commercial) */}
            <AdditionalPropertiesExtensions disabled={disableForm} onChangeActive={setLiabilityToActive} />
            <AdditionalAssetsExtensions disabled={disableForm} onChangeActive={setLiabilityToActive} />
          </Grid>
        )}
      </Grid>
    </Section>
  );
};

export default LiabilityCoverages;
